import { FormEvent, useEffect, useState } from 'react';
import { DayOfWeek } from '@weareopenr/types';
import {
  Autocomplete,
  Chip,
  InfoCard,
  LinkButton,
  MenuIcon,
  PrimaryButton,
  SecondaryButton,
  TAutocompleteOption,
  Typography,
  useTray,
} from '@weareopenr/component-library';
import { SelectableListSection } from 'src/components/SelectableListSection';
import { IMenu } from 'src/types/menu.types';
import { IMenuSet } from 'src/api/getMenuSets';
import { findImageUrl } from 'src/hooks/useMenu/formatMenu/formatMenu';
import { IPriceScheme } from 'src/types/price.types';
import { DAY_OF_WEEK_ORDER, TimetableScheduleTrack } from 'src/utils/timetables/helpers';
import styles from './AddScheduleTrayContent.module.scss';

const toDayName = (day: DayOfWeek) => `${day.charAt(0)}${day.slice(1).toLowerCase()}`;

// `label` drives the chip and input text, so it's abbreviated to keep chips compact; `content`
// overrides that with the full day name in the dropdown list, where space isn't as tight.
const DAY_OF_WEEK_OPTIONS: TAutocompleteOption[] = DAY_OF_WEEK_ORDER.map((day) => {
  const name = toDayName(day);
  return { value: day, label: name.slice(0, 3), content: <>{name}</> };
});

const pad = (value: number) => value.toString().padStart(2, '0');

// Builds every `stepMinutes` mark across the day; `withDayEnd` appends 23:59 - the last minute of
// the day, which isn't itself on any regular grid - so the day's end is always selectable.
const buildTimeOptions = (stepMinutes: number, withDayEnd = false): TAutocompleteOption[] => {
  const marks = Array.from({ length: (24 * 60) / stepMinutes }, (_, index) => {
    const minutesFromMidnight = index * stepMinutes;
    const time = `${pad(Math.floor(minutesFromMidnight / 60))}:${pad(minutesFromMidnight % 60)}`;
    return { value: time, label: time };
  });
  return withDayEnd ? [...marks, { value: '23:59', label: '23:59' }] : marks;
};

const HOUR_OPTIONS = buildTimeOptions(60);
const QUARTER_HOUR_OPTIONS = buildTimeOptions(15);
const FIVE_MINUTE_OPTIONS = buildTimeOptions(5, true);
// Every minute already lands on this grid, 23:59 included, so it needs no day-end special-case.
const ONE_MINUTE_OPTIONS = buildTimeOptions(1);

// Start time only ever calls out the day's start, End time only the day's end - each field pins
// its own boundary option to the top of the list (with a friendlier label) whenever the typed
// text is still consistent with it, regardless of which granularity tier is currently active.
const DAY_BOUNDARY = {
  start: { time: '00:00', label: 'Day Start (00:00)' },
  end: { time: '23:59', label: 'Day End (23:59)' },
} as const;

// A non-selectable divider separating the boundary option above from the plain time list below -
// `disabled: true` keeps it out of click/keyboard selection entirely.
const SUGGESTIONS_HEADER: TAutocompleteOption = {
  value: '__suggestions-header__',
  label: 'Suggestions:',
  disabled: true,
  // `colour="secondary"` is white (meant for dark surfaces), so the muted tone here comes from
  // this component's own className instead.
  content: (
    <Typography size="xsmall" weight="semi-bold" className={styles['suggestions-header']}>
      Suggestions:
    </Typography>
  ),
};

// Options get more granular the more the user has typed: hour marks by default, quarter-hours
// once they've started narrowing down the hour, every 5 minutes once they've typed past the
// colon, and every minute once they're specific enough to have started typing the minute itself.
const getGranularityOptions = (typedLength: number): TAutocompleteOption[] => {
  if (typedLength === 0) return HOUR_OPTIONS;
  if (typedLength <= 2) return QUARTER_HOUR_OPTIONS;
  if (typedLength === 3) return FIVE_MINUTE_OPTIONS;
  return ONE_MINUTE_OPTIONS;
};

// `afterTime` is the chosen start time once it's picked - End time suggestions at or before it
// can never be valid, since a schedule can't have zero or negative duration, so they're dropped
// from the list entirely rather than left selectable and caught by validation afterwards.
const getTimeOptions = (search: string, boundary: keyof typeof DAY_BOUNDARY, afterTime = ''): TAutocompleteOption[] => {
  const trimmed = search.trim();
  const granularity = getGranularityOptions(trimmed.length);
  const matched = trimmed === '' ? granularity : granularity.filter((option) => option.label.includes(trimmed));
  // 00:00 as an end time is meaningless - a schedule can't end at the instant the day starts -
  // so it's excluded here rather than from Start time's options, which it remains valid for.
  const options =
    boundary === 'end' ? matched.filter((option) => option.value !== '00:00' && option.value > afterTime) : matched;

  const { time, label } = DAY_BOUNDARY[boundary];
  if (!time.includes(trimmed) || time <= afterTime) return options;

  const boundaryOption = { value: time, label: time, content: <>{label}</> };
  return [boundaryOption, SUGGESTIONS_HEADER, ...options.filter((option) => option.value !== time)];
};

export interface AddScheduleFormValues {
  entityId: string;
  entityName: string;
  days: DayOfWeek[];
  startTime: string;
  endTime: string;
}

// The existing entry a "Manage time slot" tray is amending - see `managedSchedule` below.
export interface ManagedSchedule {
  scheduleId: string;
  entityId: string;
  entityName: string;
  startTime: string;
  endTime: string;
}

interface MenuTrackCardProps {
  id: string;
  label: string;
  name: string;
  image: string | null;
  ranges: { range_id: string; name: string }[];
}

export interface AddScheduleTrayContentProps {
  track: TimetableScheduleTrack;
  initialDay: DayOfWeek;
  menus: IMenu[];
  isMenusLoading: boolean;
  // Offered on the menu track alongside menus.
  menuSets?: IMenuSet[];
  isMenuSetsLoading?: boolean;
  priceSchemes: IPriceScheme[];
  isPriceSchemesLoading: boolean;
  // Called on every change to the form's fields, with `null` while the form is incomplete or
  // invalid, so the caller can show/hide a live preview of the pending change.
  onPreview: (values: AddScheduleFormValues | null) => void;
  // Returns whether the schedule was added, so the tray only closes on success.
  onSubmit: (values: AddScheduleFormValues) => boolean | Promise<boolean>;
  // When set, amends this entry on `initialDay` (pre-filled, day fixed) instead of adding a new one.
  managedSchedule?: ManagedSchedule;
}

export const AddScheduleTrayContent = ({
  track,
  initialDay,
  menus,
  isMenusLoading,
  menuSets = [],
  isMenuSetsLoading = false,
  priceSchemes,
  isPriceSchemesLoading,
  onPreview,
  onSubmit,
  managedSchedule,
}: AddScheduleTrayContentProps) => {
  const { closeTray } = useTray();
  // When managing, this only holds the *additional* days - the fixed `initialDay` is prepended
  // into `days` below rather than sitting in the field, so it starts out empty.
  const [selectedDays, setSelectedDays] = useState<string[]>(managedSchedule ? [] : [initialDay]);
  const [startTime, setStartTime] = useState(managedSchedule?.startTime ?? '');
  const [endTime, setEndTime] = useState(managedSchedule?.endTime ?? '');
  const [selectedEntityId, setSelectedEntityId] = useState(managedSchedule?.entityId ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTimeSearch, setStartTimeSearch] = useState('');
  const [endTimeSearch, setEndTimeSearch] = useState('');

  const isManaging = managedSchedule !== undefined;

  const selectedEntityName =
    (track === 'menu'
      ? menus.find((menu) => menu.menuId === selectedEntityId)?.name ??
        menuSets.find((menuSet) => menuSet.menu_set_id === selectedEntityId)?.name
      : priceSchemes.find((priceScheme) => priceScheme.id === selectedEntityId)?.name) ??
    (managedSchedule && managedSchedule.entityId === selectedEntityId ? managedSchedule.entityName : undefined);

  const dayOptions = isManaging
    ? DAY_OF_WEEK_OPTIONS.map((option) =>
        option.value === initialDay ? { ...option, selected: true, disabled: true } : option,
      )
    : DAY_OF_WEEK_OPTIONS;

  const days = isManaging ? [initialDay, ...selectedDays.filter((day) => day !== initialDay)] : selectedDays;

  // The time fields only ever hold a value picked from TIME_OPTIONS (or are empty), so the only
  // cross-field check actually needed here is start/end ordering.
  const isEndTimeAfterStartTime = startTime === '' || endTime === '' || endTime > startTime;
  const endTimeOrderError = !isEndTimeAfterStartTime ? 'End time must be after start time' : undefined;

  // Flags text typed into a time field that doesn't match any selectable time, so the field can
  // surface an error instead of silently rejecting the input.
  const hasNoMatchingTime = (search: string, boundary: keyof typeof DAY_BOUNDARY, afterTime = '') =>
    search.trim() !== '' && getTimeOptions(search, boundary, afterTime).length === 0;
  const startTimeError = hasNoMatchingTime(startTimeSearch, 'start') ? 'Please select a valid time' : undefined;
  const endTimeError = hasNoMatchingTime(endTimeSearch, 'end', startTime)
    ? 'Please select a valid time'
    : endTimeOrderError;

  const formValues: AddScheduleFormValues | null =
    days.length > 0 && startTime !== '' && endTime !== '' && isEndTimeAfterStartTime && selectedEntityName
      ? {
          entityId: selectedEntityId,
          entityName: selectedEntityName,
          days: days as DayOfWeek[],
          startTime,
          endTime,
        }
      : null;

  // A managed entry opens as a complete form, so Save also waits for something to actually differ
  // from the entry as it is - its entity, either time, or an additional day to apply it to.
  const isUnchanged =
    managedSchedule !== undefined &&
    selectedEntityId === managedSchedule.entityId &&
    startTime === managedSchedule.startTime &&
    endTime === managedSchedule.endTime &&
    days.length === 1;

  const canSubmit = formValues !== null && !isUnchanged;

  // Re-fires the preview whenever a field the form recomputes from changes; `onPreview` and
  // `formValues` are deliberately not deps - they're recomputed from these same fields every
  // render, so listing them too would just re-run this on every render instead of on change.
  useEffect(() => {
    onPreview(formValues);
  }, [days.join(','), startTime, endTime, selectedEntityId]);

  const handleSelectAllDays = () => setSelectedDays(DAY_OF_WEEK_OPTIONS.map((option) => option.value));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formValues || !canSubmit) return;

    setIsSubmitting(true);
    const succeeded = await onSubmit(formValues);

    if (succeeded) {
      closeTray();
    } else {
      setIsSubmitting(false);
    }
  };

  // Menus and menu sets both sit on the menu track, so they share one card.
  const renderMenuTrackCard = ({ id, label, name, image, ranges }: MenuTrackCardProps) => (
    <InfoCard
      key={id}
      label={label}
      title={name}
      size="small"
      labelColour="grape"
      image={
        image ? (
          <img className={styles['menu-image']} src={image} alt="" />
        ) : (
          <div className={styles['image-placeholder']}>
            <MenuIcon size="small" />
          </div>
        )
      }
      border="solid"
      footnote={
        ranges.length > 0
          ? ranges.map((range) => (
              <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
            ))
          : undefined
      }
      selected={selectedEntityId === id}
      onClick={() => setSelectedEntityId(id)}
    />
  );

  const daysField = (
    <Autocomplete
      multiple
      name="days"
      label={isManaging ? 'Additional days' : 'Days of week'}
      options={dayOptions}
      value={selectedDays}
      onChange={setSelectedDays}
      limitChips={3}
    />
  );

  return (
    <form
      className={styles['container']}
      onSubmit={handleSubmit}
      aria-label={isManaging ? 'Manage time slot' : 'Add schedule'}
    >
      {isManaging ? (
        <div className={styles['time-slot-heading']}>
          <Typography size="medium" weight="bold">
            Time Slot:
          </Typography>
          <Typography size="medium">
            Day - <strong>{toDayName(initialDay)}</strong>
          </Typography>
        </div>
      ) : (
        <div className={styles['days-field']}>
          {daysField}
          <LinkButton type="button" onClick={handleSelectAllDays}>
            Select all
          </LinkButton>
        </div>
      )}
      <div className={styles['time-row']}>
        <Autocomplete
          name="startTime"
          label="Start time"
          options={getTimeOptions(startTimeSearch, 'start')}
          value={startTime || undefined}
          error={startTimeError}
          onChange={(value) => setStartTime(value ?? '')}
          filter="none"
          onSearchChange={setStartTimeSearch}
        />
        <div className={styles['time-divider']} aria-hidden="true" />
        <Autocomplete
          name="endTime"
          label="End time"
          options={getTimeOptions(endTimeSearch, 'end', startTime)}
          value={endTime || undefined}
          error={endTimeError}
          onChange={(value) => setEndTime(value ?? '')}
          filter="none"
          onSearchChange={setEndTimeSearch}
        />
      </div>
      {isManaging && <div className={styles['section-divider']} aria-hidden="true" />}
      <div className={styles['entity-list']}>
        {track === 'menu' && (
          <SelectableListSection
            title="Menu"
            isLoading={isMenusLoading}
            isEmpty={menus.length === 0}
            emptyMessage="No menus are available to add. Please review the menu source try again."
          >
            <div className={styles['card-list']}>
              {menus.map((menu) =>
                renderMenuTrackCard({
                  id: menu.menuId,
                  label: 'Menu',
                  name: menu.name,
                  image: menu.image,
                  ranges: menu.ranges,
                }),
              )}
            </div>
          </SelectableListSection>
        )}
        {track === 'menu' && (isMenuSetsLoading || menuSets.length > 0) && (
          <SelectableListSection title="Menu set" isLoading={isMenuSetsLoading} isEmpty={menuSets.length === 0}>
            <div className={styles['card-list']}>
              {menuSets.map((menuSet) =>
                renderMenuTrackCard({
                  id: menuSet.menu_set_id,
                  label: 'Menu set',
                  name: menuSet.name,
                  image: findImageUrl(menuSet.assets),
                  ranges: menuSet.ranges,
                }),
              )}
            </div>
          </SelectableListSection>
        )}
        {track === 'price_scheme' && (
          <SelectableListSection
            title="Price scheme"
            isLoading={isPriceSchemesLoading}
            isEmpty={priceSchemes.length === 0}
            emptyMessage="No price schemes available to add, please create a new price scheme"
          >
            <div className={styles['card-list']}>
              {priceSchemes.map((priceScheme) => (
                <InfoCard
                  key={priceScheme.id}
                  label="Price scheme"
                  title={priceScheme.name}
                  size="small"
                  labelColour="blueberry"
                  border="solid"
                  selected={selectedEntityId === priceScheme.id}
                  onClick={() => setSelectedEntityId(priceScheme.id)}
                />
              ))}
            </div>
          </SelectableListSection>
        )}
      </div>
      {isManaging && (
        <>
          <div className={styles['section-divider']} aria-hidden="true" />
          <div className={styles['additional-days-field']}>
            <Typography size="medium" weight="bold">
              Apply to additional days:
            </Typography>
            {daysField}
          </div>
        </>
      )}
      <div className={styles['actions']}>
        <PrimaryButton type="submit" size="full-width" loading={isSubmitting} disabled={!canSubmit}>
          Save
        </PrimaryButton>
        <SecondaryButton type="button" size="full-width" buttonStyle="ghost" onClick={closeTray}>
          Cancel
        </SecondaryButton>
      </div>
    </form>
  );
};
